import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from "@angular/common/http";
import { inject } from "@angular/core";
import { AuthService } from "app/core/auth/auth.service";
import { OrganizationService } from "app/core/auth/organization.service";
import { SessionManagerService } from "app/core/auth/session-manager.service";
import { MapDraftService } from "app/modules/admin/geo-audience/services/map-draft.service";
import { AppConfigService } from "app/services/app-config.service";
import { SelectedAdvertiserService } from "app/services/selected-advertiser.service";
import { catchError, Observable, throwError } from "rxjs";

/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const authInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);
    const selectedAdvertiserService = inject(SelectedAdvertiserService);
    const appConfigService = inject(AppConfigService);
    const organizationService = inject(OrganizationService);
    const sessionManager = inject(SessionManagerService);
    const mapDrafts = inject(MapDraftService);

    // Clone the request object
    let newReq = req.clone();

    // Third-party APIs called directly from the browser (Meilisearch, the
    // Nominatim geocoder used by the city heatmap, ...) must never get our
    // own auth/org headers attached. Beyond the obvious leak concern, a
    // custom header like Authorization forces the browser into a CORS
    // preflight (OPTIONS) — most of these third-party endpoints don't
    // implement preflight handling at all, so the request gets blocked
    // outright rather than just failing auth. Same exclusion for all
    // three headers below, not just Authorization.
    const isThirdPartyRequest =
        req.url.startsWith("https://meili.dev135.abacus-consulting.net") ||
        req.url.startsWith("https://nominatim.openstreetmap.org");

    // Request
    //
    // If the access token didn't expire, add the Authorization header.
    // We won't add the Authorization header if the access token expired.
    // This will force the server to return a "401 Unauthorized" response
    // for the protected API routes which our response interceptor will
    // catch and delete the access token from the local storage while logging
    // the user out from the app.
    //if ( authService.accessToken && !AuthUtils.isTokenExpired(authService.accessToken) )
    if (authService.accessToken && !isThirdPartyRequest) {
        newReq = req.clone({
            headers: req.headers.set("Authorization", "Bearer " + authService.accessToken),
        });
    }

    if (!isThirdPartyRequest) {
        // Add X-Advertiser-ID header if an advertiser is selected
        const advertiserId = selectedAdvertiserService.getSelectedAdvertiserId();
        if (advertiserId) {
            newReq = newReq.clone({
                headers: newReq.headers.set("X-Advertiser-ID", advertiserId),
            });
        }

        // Add X-Organization-ID header: super-admin's selected org, or current user's organization (editor/admin).
        // Backend uses it to scope responses (e.g. GET /api/users/campaigns).
        const organizationId =
            appConfigService.getSelectedOrganizationId() ?? organizationService.getOrganization()?.id;
        if (organizationId) {
            newReq = newReq.clone({
                headers: newReq.headers.set("X-Organization-ID", organizationId),
            });
        }
    }

    // Response
    return next(newReq).pipe(
        catchError((error) => {
            // Catch "401 Unauthorized" responses
            if (error instanceof HttpErrorResponse && error.status === 401) {
                // No desloguear si el error 401 viene del endpoint de autenticación de APPcelerate
                // Este endpoint puede retornar 401 si las credenciales son incorrectas
                if (error.url?.includes("/geo-audience/service/auth")) {
                    return throwError(error);
                }

                // The public report viewer (/r/report) is intentionally
                // unguarded and meant to work for anonymous visitors with
                // zero session — its data comes from the URL hash snapshot,
                // never a live API call. If something there still fires an
                // authenticated request and 401s (e.g. a section whose
                // snapshot wasn't captured before sharing), the visitor was
                // never logged in to begin with — force-navigating them to
                // /sign-in only replaces a broken report with a login wall
                // they have no account for. Let the error propagate so the
                // caller's own catchError/empty-state handles it instead.
                if (typeof window !== "undefined" && window.location.pathname.startsWith("/r/report")) {
                    return throwError(error);
                }

                // Multi-session aware 401 handling:
                // Remove only the current session, not all accounts.
                const activeUserId = sessionManager.getActiveUserId();
                if (activeUserId !== null) {
                    sessionManager.removeSession(activeUserId);
                }

                const remaining = sessionManager.getAll();
                if (remaining.length > 0) {
                    // Other sessions exist — switch to the most recent one and reload
                    const mostRecent = remaining.reduce((a, b) =>
                        a.lastActiveAt > b.lastActiveAt ? a : b
                    );
                    sessionManager.switchTo(mostRecent.userId);
                    location.reload();
                } else {
                    // No sessions left — full sign out and redirect to login
                    sessionManager.removeAllSessions();
                    localStorage.clear();
                    // Redirect once the map drafts (IndexedDB) are wiped: unloading the page would abort it
                    void mapDrafts.clearAll().finally(() => (location.href = '/sign-in'));
                }
            }

            return throwError(error);
        })
    );
};
